import React from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  Button, 
  Chip, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions,
  Tooltip,
  IconButton,
  Badge,
  Stack
} from '@mui/material';
import { 
  LocalGasStation, 
  Speed, 
  CalendarToday, 
  Euro, 
  DirectionsCar,
  CheckCircle
} from '@mui/icons-material';
import { PlusCircle, FileText } from 'lucide-react';

const CarSearchResult = ({ car, isFullscreen, onAddKnowledge, darkMode, isMobile }) => {
  const compact = !isFullscreen;

  // Handler for Generate SEO button
  const handleGenerateSeo = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    if (!car || !car.vehicleid) {
      console.error("Cannot generate SEO: Missing vehicle ID");
      return;
    }
    
    // Create a message event that the internal assistant will process
    const message = `Generate seo per vehicleid: ${car.vehicleid}`;
    
    // Send the message to the chat
    window.postMessage({
      type: 'internalAssistantMessage',
      message: message
    }, '*');
  };

  return (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: compact ? 'row' : 'column',
        bgcolor: darkMode ? '#333' : '#fff',
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        border: car.highlighted 
          ? `2px solid ${darkMode ? '#6C8EFF' : '#4361EE'}` 
          : darkMode 
            ? '1px solid rgba(255,255,255,0.1)' 
            : '1px solid rgba(0,0,0,0.08)',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-3px)',
          transition: 'all 0.2s ease-in-out'
        },
        ...(isMobile && {
          position: 'relative',
          zIndex: 1100
        })
      }}
    >
      {/* Highlight Badge */}
      {car.highlighted && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Tooltip title="Added to knowledge">
            <Chip
              icon={<CheckCircle fontSize="small" />}
              label="Added"
              size="small"
              color="primary"
              sx={{
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Car Image */}
      <Box
        sx={{
          height: compact ? 160 : 200,
          width: compact ? 160 : '100%',
          backgroundColor: darkMode ? '#222' : '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {car.urlmainimage || car.image_url ? (
          <img
            src={car.urlmainimage || car.image_url}
            alt={`${car.brand} ${car.model}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.3s ease',
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/placeholder-car.jpg';
            }}
          />
        ) : (
          <DirectionsCar sx={{ fontSize: 60, color: darkMode ? 'grey.600' : 'grey.400' }} />
        )}
      </Box>

      {/* Car Details */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        flex: 1,
        p: compact ? 2 : 2.5,
        position: 'relative',
      }}>
        <Typography 
          variant={compact ? "subtitle1" : "h6"}
          gutterBottom 
          sx={{ 
            color: darkMode ? '#fff' : '#333',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 1.5,
          }}
        >
          {car.brand} {car.model}
        </Typography>

        {/* Specs */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: compact ? 1 : 1.5,
          mb: compact ? 1.5 : 2
        }}>
          {/* Additional Details Chips - Only show in fullscreen */}
          {!compact && (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mb: 2,
              mt: 'auto' 
            }}>
              {car.bodystyle && (
                <Chip 
                  label={car.bodystyle} 
                  size="small" 
                  sx={{ 
                    bgcolor: darkMode ? 'rgba(67, 97, 238, 0.2)' : 'rgba(67, 97, 238, 0.1)',
                    color: darkMode ? '#fff' : '#4361EE',
                    fontWeight: 500,
                    height: 26,
                  }} 
                />
              )}
              {car.transmission && (
                <Chip 
                  label={car.transmission} 
                  size="small" 
                  sx={{ 
                    bgcolor: darkMode ? 'rgba(67, 97, 238, 0.2)' : 'rgba(67, 97, 238, 0.1)',
                    color: darkMode ? '#fff' : '#4361EE',
                    fontWeight: 500,
                    height: 26,
                  }} 
                />
              )}
              {car.colour && (
                <Chip 
                  label={car.colour} 
                  size="small" 
                  sx={{ 
                    bgcolor: darkMode ? 'rgba(67, 97, 238, 0.2)' : 'rgba(67, 97, 238, 0.1)',
                    color: darkMode ? '#fff' : '#4361EE',
                    fontWeight: 500,
                    height: 26,
                  }} 
                />
              )}
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
          <Button
            variant={car.highlighted ? "outlined" : "contained"}
            size={compact ? "small" : "medium"}
            onClick={() => onAddKnowledge(car)}
            disabled={car.highlighted}
            startIcon={car.highlighted ? <CheckCircle /> : <PlusCircle size={16} />}
            sx={{ 
              flex: 1,
              bgcolor: car.highlighted ? 'transparent' : '#4361EE',
              color: car.highlighted ? (darkMode ? '#fff' : '#4361EE') : '#fff',
              borderColor: car.highlighted ? '#4361EE' : 'transparent',
              '&:hover': {
                bgcolor: car.highlighted ? 'transparent' : '#2D3A8C',
                borderColor: car.highlighted ? '#6C8EFF' : 'transparent',
              },
              textTransform: 'none',
              borderRadius: 1.5,
              fontWeight: 500,
            }}
          >
            {car.highlighted ? 'Added' : 'Add'}
          </Button>

          <Button
            variant="outlined"
            size={compact ? "small" : "medium"}
            onClick={handleGenerateSeo}
            startIcon={<FileText size={16} />}
            sx={{ 
              bgcolor: 'transparent',
              color: darkMode ? '#fff' : '#4361EE',
              borderColor: darkMode ? 'rgba(255,255,255,0.3)' : '#4361EE',
              '&:hover': {
                bgcolor: 'rgba(67, 97, 238, 0.1)',
                borderColor: '#4361EE',
              },
              textTransform: 'none',
              borderRadius: 1.5,
              fontWeight: 500,
            }}
          >
            Genera SEO
          </Button>
        </Stack>
      </Box>
    </Card>
  );
};

export default CarSearchResult; 